import React from 'react';
import { Backdrop, Box, Button, CircularProgress, TablePagination, Typography } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import BondTable from '../BondTable';
import { EDI_FILE_PROCESS_STATUS } from '../../Constants/AppConstants';
import { tokens, withAlpha } from '../../Common/designTokens';
import { utcToLocal } from '../../Utils/timeConversions';

const isFailedStatus = (status) =>
  String(status || '').trim().toUpperCase() === EDI_FILE_PROCESS_STATUS.FAILED;

const getStatusTone = (status) => {
  const normalizedStatus = String(status || '').trim().toUpperCase();

  if (normalizedStatus === EDI_FILE_PROCESS_STATUS.SUCCESS) {
    return {
      color: tokens.colors.buyFontColor,
      borderColor: withAlpha(tokens.colors.buyBorderColor, 0.45),
      backgroundColor: withAlpha(tokens.colors.buyBgColor, 0.12),
    };
  }

  if (normalizedStatus === EDI_FILE_PROCESS_STATUS.FAILED) {
    return {
      color: tokens.palette.danger,
      borderColor: withAlpha(tokens.palette.danger, 0.4),
      backgroundColor: withAlpha(tokens.palette.danger, 0.1),
    };
  }

  return null;
};

const renderStatus = (status) => {
  const statusLabel = String(status || '').trim();
  if (!statusLabel) return '---';

  const tone = getStatusTone(statusLabel);
  if (!tone) return statusLabel;

  return (
    <Box
      component="span"
      title={statusLabel}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '72px',
        maxWidth: '100%',
        height: '24px',
        px: 1,
        border: `1px solid ${tone.borderColor}`,
        borderRadius: '6px',
        backgroundColor: tone.backgroundColor,
        color: tone.color,
        fontSize: '11px',
        fontWeight: 700,
        lineHeight: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {statusLabel}
    </Box>
  );
};

const formatProcessingDateTime = (value) => {
  const localDateTime = utcToLocal(value);
  return localDateTime ? localDateTime.format('DDMMMYY HH:mm:ss') : '---';
};

const UTC_MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatUtcProcessingDateTime = (value) => {
  if (!value) return '---';

  const rawValue = String(value).trim();
  const utcValue = /[zZ]|[+-]\d{2}:\d{2}$/.test(rawValue)
    ? rawValue
    : `${rawValue}Z`;
  const normalizedValue = utcValue.includes('T')
    ? utcValue
    : utcValue.replace(' ', 'T');
  const utcDate = new Date(normalizedValue);

  if (Number.isNaN(utcDate.getTime())) return '---';

  const pad = (part) => String(part).padStart(2, '0');
  const day = pad(utcDate.getUTCDate());
  const month = UTC_MONTH_LABELS[utcDate.getUTCMonth()];
  const year = pad(utcDate.getUTCFullYear() % 100);
  const hours = pad(utcDate.getUTCHours());
  const minutes = pad(utcDate.getUTCMinutes());
  const seconds = pad(utcDate.getUTCSeconds());

  return `${day}${month}${year} ${hours}:${minutes}:${seconds}`;
};

const renderRightAlignedValue = (value) => (
  <Box
    component="span"
    title={value}
    sx={{
      display: 'block',
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      textAlign: 'right',
      lineHeight: '40px',
    }}
  >
    {value}
  </Box>
);

const parseProcessDateTime = (value) => {
  return utcToLocal(value);
};

const formatDuration = (startTime, endTime) => {
  const startDate = parseProcessDateTime(startTime);
  const endDate = parseProcessDateTime(endTime);

  if (!startDate || !endDate) return '---';

  const durationMs = endDate.diff(startDate);
  if (durationMs < 0) return '---';

  const totalSeconds = Math.floor(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');
};

const getEdiFileProcessColumns = (onRetryClick, retryingId) => [
  {
    key: 'actionEdiFileProcess',
    name: 'Action',
    width: 120,
    minWidth: 110,
    sortable: false,
    render: (value, rowData) => {
      if (!isFailedStatus(rowData?.status)) return '---';

      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ReplayIcon fontSize="small" />}
            disabled={retryingId === rowData.id}
            onClick={(e) => {
              e.stopPropagation();
              if (onRetryClick) onRetryClick(rowData);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: tokens.palette.primary,
              borderColor: tokens.palette.primary,
              '&:hover': {
                borderColor: tokens.palette.primary,
                backgroundColor: tokens.colors.defaultGridHoverColor,
              },
            }}
          >
            Retry
          </Button>
        </Box>
      );
    },
  },
  {
    key: 'fileName',
    name: 'File Name',
    width: 280,
  },
  {
    key: 'status',
    name: 'Status',
    width: 120,
    render: (value) => renderStatus(value),
  },
  {
    key: 'processingDateTime',
    name: 'Processing Date Time',
    width: 210,
    render: (value) => formatProcessingDateTime(value),
  },
  {
    key: 'processingDateTimeUtc',
    name: 'Processing Date Time (UTC)',
    width: 230,
    render: (value, rowData) =>
      renderRightAlignedValue(formatUtcProcessingDateTime(rowData?.processingDateTime)),
  },
  {
    key: 'id',
    name: 'ID',
    width: 80,
  },
  {
    key: 'totalRecords',
    name: 'Total Records',
    width: 135,
  },
  {
    key: 'updatedRecords',
    name: 'Updated Records',
    width: 145,
  },
  {
    key: 'insertedRecords',
    name: 'Inserted Records',
    width: 145,
  },
  {
    key: 'histUpdatedRecords',
    name: 'Hist Updated Records',
    width: 170,
  },
  {
    key: 'histInsertedRecords',
    name: 'Hist Inserted Records',
    width: 170,
  },
  {
    key: 'failedRecords',
    name: 'Failed Records',
    width: 135,
  },
  {
    key: 'errorMessage',
    name: 'Error Message',
    width: 260,
  },
  {
    key: 'startTime',
    name: 'Start Time',
    width: 210,
    render: (value) => formatProcessingDateTime(value),
  },
  {
    key: 'endTime',
    name: 'End Time',
    width: 210,
    render: (value) => formatProcessingDateTime(value),
  },
  {
    key: 'duration',
    name: 'Duration',
    width: 115,
    sortable: false,
    render: (value, rowData) => formatDuration(rowData?.startTime, rowData?.endTime),
  },
];

const EdiFileProcessTable = ({
  data,
  setData,
  sortConfig,
  onSort,
  error,
  loading,
  onRetryClick,
  retryingId,
  page,
  rowsPerPage,
  totalElements,
  onPageChange,
  onRowsPerPageChange,
  tableHeight = 'calc(100vh - 178px)',
}) => {
  const columns = getEdiFileProcessColumns(onRetryClick, retryingId);

  if (error && !loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: '400px',
        }}
      >
        <Typography
          sx={{
            color: tokens.palette.danger,
            fontSize: '16px',
            fontWeight: 500,
            marginBottom: '8px',
          }}
        >
          Error Loading EDI File Processing Details
        </Typography>
        <Typography sx={{ color: tokens.colors.fontColour50, fontSize: '14px' }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (!loading && data.length === 0 && !error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: '400px',
        }}
      >
        <Typography sx={{ color: tokens.colors.fontColour50, fontSize: '16px' }}>
          No EDI file processing data available
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          padding: '0px',
          height: tableHeight,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <BondTable
          source="ediFileProcess"
          selectedColumns={columns}
          filteredData={data}
          handleRowClick={() => {}}
          handleViewJson={() => {}}
          handleBuyClick={() => {}}
          handleCusipClick={() => {}}
          handleAllocateAccount={() => {}}
          onOrderAdded={() => {}}
          orderData={data}
          setOrderData={setData}
          sectionKey="ediFileProcess"
          onSort={onSort}
          sortConfig={sortConfig}
        />
        <Backdrop
          open={loading}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            backgroundColor: withAlpha(tokens.colors.white, 0.7),
            color: tokens.palette.primary,
          }}
        >
          <CircularProgress sx={{ color: tokens.palette.primary }} size={44} />
        </Backdrop>
      </Box>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          color: 'white',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'end',
          alignItems: 'center',
        }}
      >
        <TablePagination
          component="footer"
          sx={{
            '.MuiSelect-select': { p: '4px 24px 5px 8px !important' },
          }}
          showFirstButton
          showLastButton
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
          page={page}
          onRowsPerPageChange={onRowsPerPageChange}
          onPageChange={onPageChange}
          count={totalElements}
          labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString()}–${to.toLocaleString()} of ${count.toLocaleString()}`}
        />
      </Box>
    </>
  );
};

export default EdiFileProcessTable;
